import React, { Component, RefObject } from "react"
import trans from "trans"
import * as d3 from "d3"

import { connect } from "react-redux"

import {
    Theme,
} from "@bdfx/mergado-ui-kit"

import { RandomProduct } from "shape-edit/types"
import {
    AnyPoint,
    CollageLayoutImageType,
    CollageLayoutType,
    SettingsFormType,
    Template as TemplateType
} from "app/types/model-types"

import { GuideLines, GuidesType, ID } from "app/types/app-types"

import {
    updateInputValue as change,
} from "app/reducers/form";

import { CoordsType } from "./shape"
import { calcObjectCorners, getCursorByType, getGridMinApproachPx, getHandleSize, getTemplateSize, rotatePointAboutPoint } from "shape-edit/forms/helpers"
import { ClipMaskObject, EmptyClipMaskObject } from "shape-edit/forms/edit-clip-mask-modal"
import { renderClipMaskShape } from "template-edit/components/helper"
import { selectFormValues } from "app/reducers/form"
import { RENDERED_TYPE_BGREMOVE } from "products/types"
import { debounce, find, forEach, sortBy, uniq } from "lodash"
import { iconDisabledColor } from "template-edit/components/tools-panel"
import { canRetryImageUrl, getImageRetryDelay, withImageRetryParam } from "app/helpers/image-retry"

export const DEFAULT_FRAMES_DELAY = 1000
const { min, max, abs, sin, cos, PI, round, tan, atan2, ceil } = Math

interface Props {
    className?: string
    draggable?: boolean
    selectedCollage: CollageLayoutType
    randomProduct: RandomProduct
    layoutWidth: number
    layoutHeight: number
    isShadow: boolean
    isAnim: boolean
    framesDelay?: number
    loopsDelay?: number
    settingsForm: SettingsFormType
    templateForm: TemplateType
    guides: GuidesType
    change: typeof change
    setSelectedImageId?: Function
    setD?: Function
    selectedImageId?: ID
    setSelectedCollageId?: Function
    pushHistory?: Function
    previewMode?: boolean    //show text and do not render shadows, reflections, etc
    dx_rel?: number
    dy_rel?: number
    dw_rel?: number
    dh_rel?: number
    selectedImageAngle?: number
}

interface State {
    refType: HandleRefType
    box: DOMRect
    guideLines: GuideLines
    snappedToX: number
    snappedToY: number
    dragStartX: number
    dragStartY: number
    imageRetries: Record<string, number>   //image url -> retry count
}

type HandleRefType = "topleft" | "topright" | "bottomleft" | "bottomright" | "center" | "center-box" | "image"
const refTypes: HandleRefType[] = ["center-box", "image", "center", "topleft", "topright", "bottomleft", "bottomright"]     //should be in the binding order

class AlternativeImageSvg extends Component<Props, State> {
    wrapperRef: RefObject<SVGGElement>
    handleRefs: React.RefObject<SVGGElement | SVGRectElement | SVGImageElement>[] = []
    animRef: RefObject<SVGAnimateElement>

    protected animationLoopInterval
    imageRetryTimers: Record<string, ReturnType<typeof setTimeout>> = {}

    constructor(props) {
        super(props)

        this.state = {
            refType: 'center',
            box: null,
            guideLines: null,
            snappedToX: null,
            snappedToY: null,
            dragStartX: 0,
            dragStartY: 0,
            imageRetries: {},
        }

        this.wrapperRef = React.createRef()
        this.animRef = React.createRef()
    }

    componentDidMount(): void {
        const { draggable } = this.props
        if (draggable) {
            this.bindDragEvent(this.props.selectedImageId)
            this.bindAllImagesMoveEvents()
            //this.bindWheelEvents()
            this.updateGuidelinesForAllObjects()
            this.props.pushHistory()
        }
        this.restartAnimationLoop()
    }

    componentWillUnmount(): void {
        this.stopAnimationLoop()
        Object.values(this.imageRetryTimers).forEach(timer => clearTimeout(timer))
    }

    handleImageError = (imageUrl: string) => {
        const retry = this.state.imageRetries[imageUrl] ?? 0
        if (!canRetryImageUrl(imageUrl, retry) || this.imageRetryTimers[imageUrl]) {
            return
        }

        this.imageRetryTimers[imageUrl] = setTimeout(() => {
            delete this.imageRetryTimers[imageUrl]
            this.setState(prevState => ({ imageRetries: { ...prevState.imageRetries, [imageUrl]: retry + 1 } }))
        }, getImageRetryDelay(retry))
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (prevProps) {
            if (this.props.draggable && (
                prevProps.selectedImageId !== this.props.selectedImageId ||
                prevProps.setSelectedCollageId !== this.props.setSelectedCollageId ||
                prevProps.selectedImageAngle !== this.props.selectedImageAngle
            )) {
                this.bindDragEvent(this.props.selectedImageId)
                this.updateGuidelinesForAllObjects()
                this.bindAllImagesMoveEvents()
            }

            if (prevProps.framesDelay !== this.props.framesDelay || prevProps.loopsDelay !== this.props.loopsDelay || prevProps.isAnim !== this.props.isAnim) {
                window.onload = () => this.restartAnimationLoop()
                this.restartAnimationLoop()
            }
        }
    }

    setSelected = (newSelectedImageId: ID) => {
        if (this.props.setSelectedImageId) {
            this.props.setSelectedImageId(newSelectedImageId)
        }
    }

    getSectedImage = (selectedImageId: ID): CollageLayoutImageType => {
        return selectedImageId ? this.findImageById(selectedImageId) : null
    }

    stopAnimationLoop = () => {
        //console.debug('stop animation, clear interval')
        clearInterval(this.animationLoopInterval)
    }

    restartAnimationLoop = () => {
        const { loopsDelay, framesDelay, isAnim, randomProduct } = this.props

        if (this.animationLoopInterval) {
            this.stopAnimationLoop()
        }

        if (isAnim) {
            //console.debug('restarting animation')
            //const el: SVGAnimateElement = document.getElementById('anim') as unknown as SVGAnimateElement
            const el = this.animRef.current
            const altImages = randomProduct?.alternative_images && Array.isArray(randomProduct.alternative_images) ?
                randomProduct.alternative_images.map(i => (i.imageBlob ?? i.url)) : null

            if (el && altImages && altImages.length) {
                this.animationLoopInterval = setInterval(() => {
                    console.debug('inside interval', (loopsDelay ?? DEFAULT_FRAMES_DELAY) + altImages.length * (framesDelay ?? DEFAULT_FRAMES_DELAY))
                    el.beginElement();
                }, (loopsDelay ?? DEFAULT_FRAMES_DELAY) + altImages.length * (framesDelay ?? DEFAULT_FRAMES_DELAY))
            }
        }
    }

    updateGuidelinesForAllObjectsDebounced = debounce(() => {
        this.updateGuidelinesForAllObjects()
        this.props.pushHistory()
        this.bindAllImagesMoveEvents()
    }, 200)

    updateGuidelinesForAllObjects = () => {
        const { draggable, guides, selectedCollage, selectedImageId, layoutWidth, layoutHeight } = this.props

        if (!draggable || !guides || !guides.isSnapOn || guides.gridStepMode > 0 || guides.isShowGrid) {
            return;
        }

        let verticalGuidelines: number[] = []   //x1,..,xn
        let horizontalGuidelines: number[] = [] //y1,..,yn

        if (selectedCollage) {
            //add canvas boundaries:
            verticalGuidelines = [0, selectedCollage.rel_width]
            horizontalGuidelines = [0, selectedCollage.rel_height]

            //images boundaries  (except the active image):
            forEach(selectedCollage.images, image => {
                if (selectedImageId && `${image.id}` !== `${selectedImageId}`) {
                    const objectCorners = calcObjectCorners(image.x_rel * layoutWidth, image.y_rel * layoutHeight, image.width_rel * layoutWidth, image.height_rel * layoutHeight, (image.angle || 0))

                    verticalGuidelines.push(objectCorners.topLeft.x)
                    verticalGuidelines.push(objectCorners.middle.x)
                    verticalGuidelines.push(objectCorners.bottomRight.x)

                    horizontalGuidelines.push(objectCorners.topLeft.y)
                    horizontalGuidelines.push(objectCorners.middle.y)
                    horizontalGuidelines.push(objectCorners.bottomRight.y)

                    //if rotated, add also all image boundary points (which are not same as objectCorners):
                    if (image.angle && objectCorners.allRotatedPoints && Array.isArray(objectCorners.allRotatedPoints) && objectCorners.allRotatedPoints.length > 0) {
                        objectCorners.allRotatedPoints.forEach(p => {
                            if (!verticalGuidelines.includes(p.x)) {
                                verticalGuidelines.push(p.x)
                            }

                            if (!horizontalGuidelines.includes(p.y)) {
                                horizontalGuidelines.push(p.y)
                            }
                        })
                    }
                }
            })
        }

        const newGuidelines: GuideLines = {
            x: verticalGuidelines ? uniq(sortBy(verticalGuidelines)) : null,
            y: horizontalGuidelines ? uniq(sortBy(horizontalGuidelines)) : null,
        }

        this.setState({ guideLines: newGuidelines })
    }

    nearestDiff = (value: number): number => {
        const { guides } = this.props
        return value - round(value / guides.gridStep) * guides.gridStep
    }

    snapToGrid = (dx: number, dy: number, selectedImage: CollageLayoutImageType): { dx: number, dy: number } => {
        const { guides, layoutWidth, layoutHeight } = this.props
        const { guideLines } = this.state

        if (!guides || !guides.isSnapOn) {    //is snap possible?
            return { dx, dy }
        }

        const activeImageCorners = calcObjectCorners(
            layoutWidth * (selectedImage.x_rel + dx),
            layoutHeight * (selectedImage.y_rel + dy),
            layoutWidth * (selectedImage.width_rel),
            layoutHeight * (selectedImage.height_rel),
            (selectedImage.angle || 0)
        )

        let correctX = 0
        let correctY = 0

        let snappedToX = null
        let snappedToY = null

        const minApproachX = 0.02 * layoutWidth
        const minApproachY = 0.02 * layoutHeight

        if (!guides.isShowGrid && guides.isShowGuides && guideLines) {

            if (guideLines.x && guideLines.x.length > 0) {
                guideLines.x.forEach(x => {
                    const d1 = activeImageCorners.topLeft.x - x
                    const d2 = activeImageCorners.bottomRight.x - x
                    const d3 = activeImageCorners.middle.x - x

                    //side to side:
                    if (d1 <= minApproachX && -d1 <= 2 * minApproachX) {
                        correctX = -d1
                        snappedToX = activeImageCorners.topLeft.x - d1
                    }

                    //which one is closer to the object (left/right)?
                    if (d2 <= minApproachX && -d2 <= 2 * minApproachX && (correctX === 0 || d2 < d1)) {
                        correctX = -d2
                        snappedToX = activeImageCorners.bottomRight.x - d2
                    }

                    //only middle to middle:
                    if (d3 <= minApproachX && -d3 <= 2 * minApproachX && (correctX === 0 || d3 < correctX)) {
                        correctX = -d3
                        snappedToX = activeImageCorners.middle.x - d3
                    }
                })
            }

            if (guideLines.y && guideLines.y.length > 0) {
                guideLines.y.forEach(y => {
                    const d1 = activeImageCorners.topLeft.y - y
                    const d2 = activeImageCorners.bottomRight.y - y
                    const d3 = activeImageCorners.middle.y - y

                    if (d1 <= minApproachY && -d1 <= 2 * minApproachY) {
                        correctY = -d1
                        snappedToY = activeImageCorners.topLeft.y - d1
                    }

                    //which one is closer to the object (bottom/top)?
                    if (d2 <= minApproachY && -d2 <= 2 * minApproachY && (correctY === 0 || d2 < d1)) {
                        correctY = -d2
                        snappedToY = activeImageCorners.bottomRight.y - d2
                    }

                    if (d3 <= minApproachY && -d3 <= 2 * minApproachY && (correctY === 0 || d3 < correctY)) {
                        correctY = -d3
                        snappedToY = activeImageCorners.middle.y - d3
                    }
                })
            }
        } else if (guides.isShowGrid) {
            const minApproachX = getGridMinApproachPx(layoutWidth) / 10
            const minApproachY = getGridMinApproachPx(layoutHeight) / 10

            //horizontal:
            const d1 = this.nearestDiff(activeImageCorners.topLeft.x)
            const d2 = this.nearestDiff(activeImageCorners.bottomRight.x)
            const d3 = this.nearestDiff(activeImageCorners.middle.x)

            if (d1 <= minApproachX && -d1 <= minApproachX) {
                correctX = -d1
            }

            if (d2 <= minApproachX && -d2 <= minApproachX && (correctX === 0 || d2 < d1)) {
                correctX = -d2
            }

            if (d3 <= minApproachX && -d3 <= minApproachX && (correctX === 0 || d3 < correctX)) {
                correctX = -d3
            }

            //vertical:
            const e1 = this.nearestDiff(activeImageCorners.topLeft.y)
            const e2 = this.nearestDiff(activeImageCorners.bottomRight.y)
            const e3 = this.nearestDiff(activeImageCorners.middle.y)

            if (e1 <= minApproachY && -e1 <= minApproachY) {
                correctY = -e1
            }

            if (e2 <= minApproachY && -e2 <= minApproachY && (correctY === 0 || e2 < e1)) {
                correctY = -e2
            }

            if (e3 <= minApproachY && -e3 <= minApproachY && (correctY === 0 || e3 < correctY)) {
                correctY = -e3
            }
        }

        this.setState({
            snappedToX: snappedToX,
            snappedToY: snappedToY,
        })

        correctX = correctX / layoutWidth
        correctY = correctY / layoutHeight

        return {
            dx: dx + correctX,
            dy: dy + correctY,
        }
    }

    getNewPointPos = (event): AnyPoint => {
        const { box } = this.state

        return {
            x: (event.sourceEvent.clientX - this.state.dragStartX) / box.width,
            y: (event.sourceEvent.clientY - this.state.dragStartY) / box.height,
        }
    }

    getDeltaByType = (refType: HandleRefType, d: CoordsType, selectedImage: CollageLayoutImageType): { dw_rel: number, dh_rel: number, dx_rel: number, dy_rel: number } => {
        switch (refType) {
            case 'bottomright':
                return {
                    dw_rel: +d.x,
                    dh_rel: +d.y,
                    dx_rel: .5 * d.x,
                    dy_rel: .5 * d.y,
                }
            case 'bottomleft':
                return {
                    dw_rel: -d.x,
                    dh_rel: +d.y,
                    dx_rel: .5 * d.x,
                    dy_rel: .5 * d.y,
                }
            case 'topright':
                return {
                    dw_rel: +d.x,
                    dh_rel: -d.y,
                    dx_rel: .5 * d.x,
                    dy_rel: .5 * d.y,
                }
            case 'topleft':
                return {
                    dw_rel: -d.x,
                    dh_rel: -d.y,
                    dx_rel: .5 * d.x,
                    dy_rel: .5 * d.y,
                }
            default:
                return {
                    dw_rel: 0,
                    dh_rel: 0,
                    dx_rel: d.x,
                    dy_rel: d.y,
                }
        }
    }

    dragStarted = (event, refType: HandleRefType, imageId: ID) => {
        const { draggable, layoutWidth, layoutHeight } = this.props

        const target = document.getElementById('collage-layout-selected-box')
        if (draggable && target) {
            this.setSelected(imageId)
            const boundingClientRect = target.getBoundingClientRect()

            this.setState({
                dragStartX: event.sourceEvent.clientX,
                dragStartY: event.sourceEvent.clientY,
                refType: refType,
                box: boundingClientRect,
            })

            this.props.setD({
                dx_rel: 0,
                dy_rel: 0,
                dw_rel: 0,
                dh_rel: 0
            })
        }
    }

    dragged = (event, imageId: ID) => {
        const { refType, box } = this.state
        const { draggable, layoutWidth, layoutHeight } = this.props

        //console.debug(`inside dragged: ref ${refType} image ${imageId}`)

        if (draggable && event && event.sourceEvent && event.sourceEvent.clientX && event.sourceEvent.clientY && box.width && box.height) {
            const selectedImage = this.findImageById(imageId)
            const newPoint = this.getNewPointPos(event) //in range [0..1,0..1]

            if (refType === 'center' || refType === 'center-box' || refType === 'image') {
                const snaptogrid = this.snapToGrid(newPoint.x, newPoint.y, selectedImage)

                this.props.setD({
                    dx_rel: snaptogrid.dx,
                    dy_rel: snaptogrid.dy
                })
            } else {
                const snaptogrid = this.snapToGrid(newPoint.x, newPoint.y, selectedImage)
                const rotatedPoint = rotatePointAboutPoint({ x: snaptogrid.dx, y: snaptogrid.dy }, {x: 0, y: 0}, -selectedImage.angle, false)
                const d = this.getDeltaByType(refType, rotatedPoint, selectedImage)
                const rotatedPos = rotatePointAboutPoint({ x: d.dx_rel, y: d.dy_rel }, {x: 0, y: 0}, selectedImage.angle, false)

                this.props.setD({
                    dw_rel: d.dw_rel,
                    dh_rel: d.dh_rel,
                    dx_rel: rotatedPos.x,
                    dy_rel: rotatedPos.y,
                })
            }
        }
    }

    dragEnded = (event, imageId: ID) => {
        const { refType } = this.state
        const { draggable, dx_rel, dy_rel, dw_rel, dh_rel } = this.props

        if (draggable) {
            const el = this.getElByTypeAndImageId(imageId, refType)
            d3.select(el).classed("active", false)

            this.setState({
                refType: null,
                dragStartX: 0,
                dragStartY: 0,
            })

            this.props.setD({
                dx_rel: 0,
                dy_rel: 0,
                dw_rel: 0,
                dh_rel: 0
            })

            const selectedImage = this.findImageById(imageId)
            this.props.change('collageLayoutsForm', this.findImageByIdFormIndex(), {
                ...selectedImage,
                x_rel: selectedImage.x_rel + dx_rel,
                y_rel: selectedImage.y_rel + dy_rel,
                width_rel: abs(selectedImage.width_rel + dw_rel),
                height_rel: abs(selectedImage.height_rel + dh_rel),
            })


            this.updateGuidelinesForAllObjectsDebounced()    //tady by melo byt debounced!!!!!
        }
    }

    getRefByTypeAndImageId = (imageId: ID, refType: HandleRefType): React.RefObject<any> => {
        const refString = `ref_${refType}_${imageId}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    getElByTypeAndImageId = (imageId: ID, refType: HandleRefType): SVGGElement | SVGRectElement | SVGImageElement => {
        const ref = this.getRefByTypeAndImageId(imageId, refType)
        return ref.current
    }

    handleStepWheelZoom = (event) => {
        const { selectedImageId } = this.props

        if (selectedImageId) {
            event.preventDefault()
            const selectedImage = this.findImageById(selectedImageId)
            const selectedImageIndex = this.findImageByIdFormIndex()
            const updatedValue = {
                ...selectedImage,
                width_rel: selectedImage.width_rel * event.transform.k,
                height_rel: selectedImage.height_rel * event.transform.k,
            }
            this.props.change('collageLayoutsForm', selectedImageIndex, updatedValue)
        }
    }

    handleWheelZoom = (event) => {
        const { selectedImageId } = this.props
        if (selectedImageId) {
            event.preventDefault()
        }
    }

    bindWheelEvents = () => {
        const { settingsForm, draggable } = this.props
        const el = this.wrapperRef.current
        /*if(draggable && el && d3.select(el) && !settingsForm.disable_wheelzoom) {
            d3.select(el)
                .call(
                    d3.zoom()
                        .scaleExtent([MIN_ZOOM, MAX_ZOOM])
                        .on("zoom", this.handleWheelZoom)
                )
                d3.select(el)
                    .on("wheel", this.handleStepWheelZoom)
        }*/
    }

    bindDragEvent = (imageId: ID) => {
        const { draggable } = this.props
        if (imageId && draggable) {
            const selectedImage = this.findImageById(imageId)

            if (selectedImage) {    //when image is deleted but it is still in the array of images
                const angle = selectedImage.angle || 0

                refTypes.forEach(refType => {
                    const el = this.getElByTypeAndImageId(imageId, refType)
                    if (el) {
                        el.style.cursor = getCursorByType(refType, angle)
                        d3.select(el)
                            .call(
                                d3.drag()
                                    .on("start", (event) => this.dragStarted(event, refType, imageId))
                                    .on("drag", (event) => this.dragged(event, imageId))
                                    .on("end", (event) => this.dragEnded(event, imageId))
                            )
                    }
                })
            }
        }
    }

    bindAllImagesMoveEvents = () => {
        const { selectedCollage } = this.props
        const images = selectedCollage ? selectedCollage.images : []

        Object.keys(images).forEach(k => {
            const i = images[k]

            const el = this.getElByTypeAndImageId(i.id, 'image')
            if (el) {
                el.style.cursor = getCursorByType('image', i.angle || 0)
                d3.select(el)
                    .call(
                        d3.drag()
                            .on("start", (event) => this.dragStarted(event, 'image', i.id))
                            .on("drag", (event) => this.dragged(event, i.id))
                            .on("end", (event) => this.dragEnded(event, i.id))
                    )
            }

            const el2 = this.getElByTypeAndImageId(i.id, 'center-box')
            if (el2) {
                el2.style.cursor = getCursorByType('center-box', i.angle || 0)
                d3.select(el)
                    .call(
                        d3.drag()
                            .on("start", (event) => this.dragStarted(event, 'center-box', i.id))
                            .on("drag", (event) => this.dragged(event, i.id))
                            .on("end", (event) => this.dragEnded(event, i.id))
                    )
            }

        })
    }

    findImageById = (selectedImageId: ID): CollageLayoutImageType => {
        const { selectedCollage } = this.props
        const images = selectedCollage ? selectedCollage.images : []
        return find(images, (i) => {
            return `${i.id}` === `${selectedImageId}`
        })
    }

    findImageByIdFormIndex = (): string => {
        const { selectedCollage, selectedImageId } = this.props
        return `${selectedCollage.id}.images.${selectedImageId}`
    }

    updateCollageImagePoints = (newPointsAbs) => {
        const { layoutWidth, layoutHeight, selectedCollage, selectedImageId } = this.props
        const { dx_rel, dy_rel, dw_rel, dh_rel } = this.props

        const selectedImage = this.findImageById(selectedImageId)

        const scaleX = selectedCollage.rel_width * layoutWidth
        const scaleY = selectedCollage.rel_height * layoutHeight

        const width = scaleX * (selectedImage.width_rel + dw_rel)
        const height = scaleY * (selectedImage.height_rel + dh_rel)

        let updatedPoints = []
        newPointsAbs.forEach(point => {
            updatedPoints.push({
                x: point.x / width,
                y: point.y / height,
                cx: typeof point.cx !== 'undefined' ? point.cx / width : undefined,
                cy: typeof point.cy !== 'undefined' ? point.cy / height : undefined,
            })
        })

        const newPoints = JSON.stringify(updatedPoints)
        this.props.change('collageLayoutsForm', `${this.findImageByIdFormIndex()}.points`, newPoints)
    }

    renderImage = (image: CollageLayoutImageType, animId: string | null, onlyOneImageInCollage: boolean) => {
        const {
            layoutWidth, layoutHeight, randomProduct, draggable, selectedCollage, selectedImageId,
            templateForm, isShadow, isAnim, framesDelay, loopsDelay, previewMode,
            dx_rel, dy_rel, dw_rel, dh_rel, guides
        } = this.props

        const { guideLines, snappedToX, snappedToY } = this.state

        const templateSize = getTemplateSize(templateForm)

        const scalePx = layoutWidth / templateSize.width

        if (!image) {
            return <g />
        }

        const className = this.props.className || ''
        const isSelected = `${image.id}` === `${selectedImageId}`

        const imageXrel = image.x_rel + (isSelected ? dx_rel : 0)
        const imageYrel = image.y_rel + (isSelected ? dy_rel : 0)

        const imageWrel = image.width_rel + (isSelected ? dw_rel : 0)
        const imageHrel = image.height_rel + (isSelected ? dh_rel : 0)

        const IconArrows: JSX.Element = <g stroke={`#ccc`}>
            <path d="M40 20q0 .6-.4 1l-5.7 5.7q-.5.4-1 .4t-1-.4-.5-1v-2.8h-8.5v8.5h2.8q.6 0 1 .5t.4 1-.4 1L21 39.6q-.4.4-1 .4t-1-.4l-5.7-5.7q-.4-.5-.4-1t.4-1 1-.5h2.8v-8.5H8.6v2.8q0 .6-.5 1t-1 .4-1-.4L.4 21q-.4-.4-.4-1t.4-1l5.7-5.7q.5-.4 1-.4t1.1.4.4 1v2.8h8.5V8.6h-2.8q-.6 0-1-.5t-.4-1 .4-1L19 .4q.4-.4 1-.4t1 .4l5.7 5.7q.4.5.4 1t-.4 1-1 .5h-2.8v8.5h8.5v-2.8q0-.6.5-1t1-.4 1 .4l5.7 5.7q.4.4.4 1z" />
        </g>

        const dashArrayInner = '4 4'
        const handleSize = ceil(layoutWidth / 40)

        const marginX = selectedCollage.rel_width * (image.padding_px || 0) * scalePx
        const marginY = selectedCollage.rel_height * (image.padding_px || 0) * scalePx

        const scaleX = selectedCollage.rel_width * layoutWidth
        const scaleY = selectedCollage.rel_height * layoutHeight

        const width = scaleX * imageWrel
        const height = scaleY * imageHrel

        const x = scaleX * imageXrel
        const y = scaleY * imageYrel

        const widthMinusMargins = width - 2 * marginX
        const heightMinusMargins = height - 2 * marginY

        const mainImage = randomProduct ?
            (templateForm.type === RENDERED_TYPE_BGREMOVE && randomProduct.bgremovedImage ? randomProduct.bgremovedImage : (randomProduct.imageBlob ?? randomProduct.image_link)) :
            null

        const xlinkhref = randomProduct ? (
            `${image.level}` === '0' ? mainImage : (randomProduct.alternative_images && randomProduct.alternative_images[image.level - 1]) ?
                (randomProduct.alternative_images[image.level - 1].imageBlob ?? randomProduct.alternative_images[image.level - 1].url)
                : ''
        ) : ''

        const animxlinkhref = xlinkhref //mainImage //todo change this each period

        const textXPos = width / 2
        const textYPos = height / 2 - height / 16

        const imageXOffset = width * (image.x_offset ?? 0)         //image.x_offset is relative offset (0-1)
        const imageYOffset = height * (image.y_offset ?? 0)

        const clipPathId = draggable ? `collage-clip-path-edit-${selectedCollage.id}-${image.id}` :
            (previewMode ? `collage-clip-path-select-${selectedCollage.id}-${image.id}` : `collage-clip-path-canvas-${selectedCollage.id}-${image.id}`)

        let thisWidth = widthMinusMargins
        let thisHeight = heightMinusMargins

        if (image.shape === 'circle') {
            const minSide = min(thisWidth, thisHeight)
            thisWidth = minSide
            thisHeight = minSide
        }

        const points = image.points ? JSON.parse(image.points) : [] //bezier nebo polygon
        let scaledPoints = [...points]  //relative units to pixels
        scaledPoints.forEach((point, index) => {
            points[index].x = scaleX * imageWrel * point.x
            points[index].y = scaleY * imageHrel * point.y

            if (typeof point.cx !== 'undefined') {
                points[index].cx = scaleX * imageWrel * point.cx
            }

            if (typeof point.cy !== 'undefined') {
                points[index].cy = scaleY * imageHrel * point.cy
            }
        })

        //preview object shape:
        const maskObject: ClipMaskObject = {
            ...EmptyClipMaskObject,
            width: thisWidth,
            height: thisHeight,
            padding: image.padding_px,
            /* angle: image.angle, */
            x: 0,
            y: 0,
            isFilled: false,
            strokeWidth: 5,
            type: image.shape ? image.shape : 'rect',
            points: scaledPoints
        }

        const altImages = isAnim && randomProduct?.alternative_images && Array.isArray(randomProduct.alternative_images) ?
            randomProduct.alternative_images.map(i => i.url) : null

        const baseImagelink = isAnim ? animxlinkhref : xlinkhref
        const imagelink = withImageRetryParam(baseImagelink, this.state.imageRetries[baseImagelink] ?? 0)

        const fontSize = (layoutWidth+layoutHeight)/40

        const activeImageCorners = draggable ? calcObjectCorners(imageXrel*layoutWidth, imageYrel*layoutHeight, imageWrel*layoutWidth, imageHrel*layoutHeight, image.angle || 0) : null

        const guideCrossSize = getHandleSize() * 2
        const guideLineStrokeWidth = guideCrossSize / 10

        return <g key={`g_${image.id}`} ref={this.wrapperRef} className={'svg-img ' + className} style={{ zIndex: 888 + image.level }}>
            <g transform={`translate(${x - .5 * width}, ${y - .5 * height}) rotate(${image.angle || 0}, ${.5 * width}, ${.5 * height})`}>
                <g>
                    <rect
                        ref={this.getRefByTypeAndImageId(image.id, 'center-box')}
                        onClick={() => this.setSelected(image.id)}
                        className='alt-image-layout'
                        key={image.id}
                        width={width}
                        height={height}
                        fillOpacity={draggable ? .5 : 1}
                        fill={image.bg_color_hex || 'transparent'}
                        style={{
                            opacity: image.alpha ? image.alpha : 1
                        }}
                    />

                    {imagelink !== '' &&
                        <g clipPath={`url(#${clipPathId})`} transform={`translate(${marginX}, ${marginY})`}>
                            <image
                                className="this-image-edit"
                                ref={this.getRefByTypeAndImageId(image.id, 'image')}
                                id={draggable ? 'alt' + image.id : undefined}
                                onClick={() => this.setSelected(image.id)}
                                width={widthMinusMargins}
                                height={heightMinusMargins}
                                xlinkHref={imagelink}
                                onError={() => this.handleImageError(baseImagelink)}
                                filter={(!previewMode && isShadow && image.level < 1) ? 'url(#shadow-source-image)' : ''}
                                style={{
                                    opacity: image.alpha ? image.alpha : 1,
                                    transformOrigin: `${.5 * widthMinusMargins}px ${.5 * heightMinusMargins}px`,
                                    pointerEvents: 'all',
                                }}
                                transform={`translate(${imageXOffset}, ${imageYOffset}), scale(${image.scale ?? 1})`}>
                                {animId && altImages && altImages.length && (image.level > 0 || onlyOneImageInCollage) &&
                                    <animate
                                        id="anim"
                                        ref={this.animRef}
                                        /* begin={`0;${animId}.end+${loopsDelay / 1000}`} */
                                        /* begin={loopsDelay ? `0;anim.end+${loopsDelay / 1000}` : undefined} */
                                        dur={altImages.length * (framesDelay ?? DEFAULT_FRAMES_DELAY) / 1000}
                                        attributeName="href"
                                        values={altImages.join(';')}
                                        repeatCount={loopsDelay ? '1' : 'indefinite'}
                                    />
                                }
                            </image>
                        </g>
                    }

                    {!previewMode && templateForm.has_reflection && imagelink !== '' &&  //!isAnim && image.level < 1
                        <g
                            className={'shape-reflection'}
                            clipPath={`url(#${clipPathId})`}
                            transform={`translate(0, ${templateForm.reflection_offset}), scale(1, -1), skewX(${templateForm.reflection_angle}), translate(${round(tan(PI * templateForm.reflection_angle / 180) * height)}, ${-2 * height})`}>

                            {/* background reflection: */}
                            <rect
                                width={width}
                                height={height}
                                fillOpacity={draggable ? .5 : 1}
                                fill={image.bg_color_hex || 'transparent'}
                                style={{
                                    opacity: image.alpha ? image.alpha : 1
                                }}
                            />

                            {/* image reflection */}
                            <image
                                width={widthMinusMargins}
                                height={heightMinusMargins}
                                xlinkHref={imagelink}
                                style={{
                                    opacity: 1 - (templateForm.reflection_opacity || 0) / 100,
                                    transformOrigin: `${.5 * widthMinusMargins}px ${.5 * heightMinusMargins}px`,
                                }}
                                transform={`translate(${imageXOffset}, ${imageYOffset}), scale(${image.scale ?? 1})`}
                            />
                        </g>
                    }
                </g>


                {previewMode &&
                    <g>
                        {image.level > 0 ?
                            <AltImageText key={'image' + image.id} x={textXPos} y={textYPos} idx={image.level} onClick={() => this.setSelected(image.id)} fontSize={fontSize} /> :
                            <MainText key={'image' + image.level} x={textXPos} y={textYPos} onClick={() => this.setSelected(image.id)} fontSize={fontSize} />}
                        {(image.alpha > 0 && image.alpha < 1) &&
                            <text
                                transform={`translate(${textXPos}, ${textYPos})`}
                                dominantBaseline={"central"}
                                textAnchor={"middle"}
                                fill={'#444'}
                                y={22}
                                style={{ fontWeight: 600, fontSize: `${fontSize}px`, pointerEvents: 'none' }}>a={image.alpha}
                            </text>
                        }
                    </g>
                }

                {(draggable || previewMode) &&
                    <g>
                        {(marginX || marginY) &&
                            <rect
                                width={width}
                                height={height}
                                stroke={Theme.steelblue}
                                strokeWidth={1}
                                fill={'none'} />
                        }

                        <rect
                            x={marginX}
                            y={marginY}
                            width={widthMinusMargins}
                            height={heightMinusMargins}
                            strokeDasharray={dashArrayInner}
                            stroke={Theme.steelblue}
                            strokeWidth={1}
                            fill={'none'} />

                        <g transform={`translate(${width / 2}, ${height / 2})`} style={{ pointerEvents: draggable && isSelected ? 'auto' : 'none' }}>
                            {renderClipMaskShape(maskObject, true, false, 0, 0, undefined, true, draggable && isSelected ? this.updateCollageImagePoints : undefined, image.angle)}
                        </g>
                    </g>
                }

                {(draggable && isSelected) &&
                    <g>
                        <g>
                            <rect
                                ref={this.getRefByTypeAndImageId(image.id, 'topleft')}
                                className={`resizable-handle topleft`}
                                width={handleSize}
                                height={handleSize}
                                transform={`translate(${-.5 * handleSize}, ${-.5 * handleSize})`} />
                            <rect
                                ref={this.getRefByTypeAndImageId(image.id, 'topright')}
                                className={`resizable-handle topright`}
                                width={handleSize}
                                height={handleSize}
                                transform={`translate(${width - .5 * handleSize}, ${-.5 * handleSize})`} />
                            <rect
                                ref={this.getRefByTypeAndImageId(image.id, 'bottomleft')}
                                className={`resizable-handle bottomleft`}
                                width={handleSize}
                                height={handleSize}
                                transform={`translate(${-.5 * handleSize}, ${height - .5 * handleSize})`} />
                            <rect
                                ref={this.getRefByTypeAndImageId(image.id, 'bottomright')}
                                className={`resizable-handle bottomright`}
                                width={handleSize}
                                height={handleSize}
                                transform={`translate(${width - .5 * handleSize}, ${height - .5 * handleSize})`} />
                            <g
                                ref={this.getRefByTypeAndImageId(image.id, 'center')}
                                className={'arrows'}
                                fill={Theme.blue}
                                style={{ zIndex: 999999 }}
                                transform={`translate(${.5 * width - 20}, ${.5 * height - 20})`}>
                                {IconArrows}
                            </g>
                        </g>
                    </g>
                }
            </g>

            {guides && guides.isShowGuides && !guides.isShowGrid && guideLines && draggable && guideLines.x && guideLines.y && (dx_rel || dy_rel || dw_rel || dh_rel) &&
                <React.Fragment>
                    {snappedToX !== null &&
                        <line
                            x1={snappedToX}
                            x2={snappedToX}
                            y1={0}
                            y2={layoutHeight}
                            style={guideLinesStyle(guideLineStrokeWidth)}
                        />
                    }

                    {snappedToY !== null &&
                        <line
                            x1={0}
                            x2={layoutWidth}
                            y1={snappedToY}
                            y2={snappedToY}
                            style={guideLinesStyle(guideLineStrokeWidth)}
                        />
                    }

                    {/* {guideLines.y.map((y, hkey) => {
                        return guideLines.x.map((x, vkey) => <React.Fragment key={`collage_guideline_cross${hkey}_${vkey}`}>
                            <circle
                                cx={x * layoutWidth}
                                cy={y * layoutHeight}
                                r={guideCrossSize / 3}
                                strokeWidth={guideCrossSize / 100}
                                stroke={Theme.blue}
                                fill='none'
                            />
                            <line
                                x1={0}
                                x2={layoutWidth}
                                y1={y * layoutHeight}
                                y2={y * layoutHeight}
                                style={guideLinesStyle(guideLineStrokeWidth, Theme.steelblue)}
                            />
                            <line
                                x1={x * layoutWidth}
                                x2={x * layoutWidth}
                                y1={0}
                                y2={layoutHeight}
                                style={guideLinesStyle(guideLineStrokeWidth, Theme.steelblue)}
                            />
                        </React.Fragment>
                        )
                    })} */}
                </React.Fragment>
            }

            {draggable && guides && guides.isShowGrid &&
                <rect width="100%" height="100%" fill="url(#grid)" style={{ pointerEvents: "none" }} />
            }

            {/* {activeImageCorners &&
                <g className="debug-only">
                    <rect
                        x={activeImageCorners.topLeft.x}
                        y={activeImageCorners.topLeft.y}
                        width={abs(activeImageCorners.topLeft.x - activeImageCorners.bottomRight.x)}
                        height={abs(activeImageCorners.topLeft.y - activeImageCorners.bottomRight.y)}
                        stroke={Theme.green}
                        fill="none"
                        strokeWidth={3}
                    />
                    <line
                        x1={ (activeImageCorners.middle.x - abs(activeImageCorners.topLeft.x - activeImageCorners.bottomRight.x) / 2)}
                        x2={ (activeImageCorners.middle.x + abs(activeImageCorners.topLeft.x - activeImageCorners.bottomRight.x) / 2)}
                        y1={ activeImageCorners.middle.y}
                        y2={ activeImageCorners.middle.y}
                        style={{ strokeWidth: 3, stroke: Theme.green }}
                    />
                    <line
                        x1={ activeImageCorners.middle.x}
                        x2={ activeImageCorners.middle.x}
                        y1={ (activeImageCorners.middle.y - abs(activeImageCorners.topLeft.y - activeImageCorners.bottomRight.y) / 2)}
                        y2={ (activeImageCorners.middle.y + abs(activeImageCorners.topLeft.y - activeImageCorners.bottomRight.y) / 2)}
                        style={{ strokeWidth: 3, stroke: Theme.green }}
                    />
                    {activeImageCorners.allRotatedPoints &&
                        activeImageCorners.allRotatedPoints.map((p, j) => <circle key={`point_${j}`} cx={p.x} cy={p.y} rx={20} ry={20} fill={Theme.green} />)
                    }
                </g>
            } */}
        </g>
    }

    render() {
        const { selectedCollage, selectedImageId, isAnim, framesDelay, loopsDelay, randomProduct } = this.props

        const animId = selectedCollage && isAnim && randomProduct && randomProduct.alternative_images ? `anim-${selectedCollage.id}` : null

        const sortedImages = selectedCollage ? sortBy(selectedCollage.images, [(image) => {
            return new Number(image.priority)
        }]).reverse() : []


        const onlyOneImageInCollage = selectedCollage ? (sortedImages.length === 1) : null

        const mapped = sortedImages.map((image) => {
            //return `${selectedImageId}` !== `${image.id}` ? this.renderImage(image, animId, onlyOneImageInCollage) : <g key={`selected_${image.id}`} />;  //exclude selected
            return this.renderImage(image, animId, onlyOneImageInCollage)
        })

        const selectedImage = this.findImageById(selectedImageId)

        return <g>
            {/* {animId && loopsDelay && framesDelay &&
                <rect fill={"blue"} x={0} y={0} width={40} height={40}>
                    <animate
                        id={animId}
                        begin={`0;${animId}.end+${loopsDelay / 1000}`}
                        dur={`${(randomProduct.alternative_images.length * framesDelay) / 1000}s`}
                        attributeName="visibility"
                        attributeName="opacity"
                        from="hide"
                        to="hide"
                        from="0"
                        to="1"
                        repeatCount="indefinite"
                        repeatCount="1"
                        onAnimationEnd={(e) => { console.log('animation ended') }}
                        onAnimationStart={(e) => { console.log('animation started') }}
                        onAnimationIteration={(e) => { console.log('animation iteration') }}
                    />
                </rect>
            } */}
            {selectedCollage && selectedCollage.images && mapped}
            {/* {selectedImage && this.renderImage(selectedImage, animId, onlyOneImageInCollage)} */}       {/* selected image on top */}
        </g>
    }
}

const AltImageText = ({ x, y, idx, onClick, fontSize }) => (
    <text
        transform={`translate(${x}, ${y})`}
        dominantBaseline={"central"}
        textAnchor={"middle"}
        fill={'#333'}
        className="unselectable"
        style={{ fontWeight: 600, fontSize: `${fontSize}px`, pointerEvents: typeof onClick === 'function' ? 'auto' : 'none', cursor: 'pointer'  }}
        onClick={onClick}
    >{trans('alt_image')} {idx}
    </text>
)

const MainText = ({ x, y, onClick, fontSize }) => (
    <text
        transform={`translate(${x}, ${y})`}
        dominantBaseline={"central"}
        textAnchor={"middle"}
        fill={'#444'}
        className="unselectable"
        style={{ fontWeight: 600, fontSize: `${fontSize}px`, pointerEvents: typeof onClick === 'function' ? 'auto' : 'none', cursor: 'pointer'  }}
        onClick={onClick}
    >{trans('main_image').split(' ').map((w, k) => <tspan key={k} dy={k*fontSize} x={0}>{w}</tspan>)}
    </text>
)

const guideLinesStyle = (guideLineStrokeWidth: number, color: string = iconDisabledColor) => ({
    stroke: color,
    strokeWidth: `${guideLineStrokeWidth}px`,
})


function mapStateToProps(state) {
    return {
        settingsForm: selectFormValues(state, 'settingsForm'),
        templateForm: selectFormValues(state, 'templateForm'),
        guides: state.guides,
    }
}

export default connect(mapStateToProps, {
    change,
})(AlternativeImageSvg)